import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { MIN_PASSWORD_LENGTH } from '@nexus/shared';

import { ApiError, apiSend } from '../../lib/api';
import { Alert, Button, Card, PageHeader, TextField } from '../../ui';

/**
 * Ask for a reset link.
 *
 * Always reports the same thing, whatever the address. The API answers 204
 * whether or not an account exists, so there is nothing to branch on here:
 * showing an error for an unknown address would reinstate the enumeration
 * oracle the API route exists to remove.
 */
export function ForgotPassword(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    await apiSend('POST', '/auth/forgot-password', { email }).catch(() => undefined);
    setBusy(false);
    setSent(true);
  }

  if (sent) {
    return (
      <>
        <PageHeader eyebrow="Nexus" title="Check your email" />
        <Card variant="feature">
          <p>
            If <strong>{email}</strong> has an account, a reset link is on its way. It works once
            and expires in an hour.
          </p>
          <p className="muted">
            We say the same thing whether or not that address has an account — otherwise this page
            would tell anybody who asked which addresses are registered.
          </p>
          <div className="actions">
            <Link className="btn btn-ghost" to="/login">
              Back to sign in
            </Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Nexus"
        title="Reset your password"
        lead="We send a link that works once and expires in an hour."
      />
      <Card variant="feature">
        <form onSubmit={submit} className="form">
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send a reset link'}
          </Button>
        </form>
        <p className="muted">
          <Link to="/login">Back to sign in</Link>
        </p>
      </Card>
    </>
  );
}

/** Redeem a reset link. */
export function ResetPassword(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', '/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <>
        <PageHeader eyebrow="Nexus" title="That link did not work" />
        <Card>
          <Alert tone="critical">That link is missing its reset code.</Alert>
          <div className="actions">
            <Link className="btn btn-ghost" to="/forgot-password">
              Ask for a new one
            </Link>
          </div>
        </Card>
      </>
    );
  }

  if (done) {
    return (
      <>
        <PageHeader eyebrow="Nexus" title="Password changed" />
        <Card variant="feature">
          <Alert tone="positive">
            Every session that account had has been signed out, including any you did not start.
          </Alert>
          <div className="actions">
            <Link className="btn btn-primary" to="/login">
              Sign in with the new password
            </Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Nexus" title="Choose a new password" />
      <Card variant="feature">
        <form onSubmit={submit} className="form">
          <TextField
            label="New password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={`At least ${MIN_PASSWORD_LENGTH} characters. Changing it signs out every session on the account.`}
            {...(error ? { 'aria-invalid': true } : {})}
          />
          {error && <Alert tone="critical">{error}</Alert>}
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Change password'}
          </Button>
        </form>
      </Card>
    </>
  );
}
